import { GetPersonUser } from '@esportal/esportal-feature-app-root/packages/portal-service/src/models/portal';
import { ThemeProvider } from '@volkswagen-onehub/components-core';
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { ErrorScreen } from './components/screen/error';
import { LoadingScreen } from './components/screen/loading';
import { ThankyouScreen } from './components/screen/thankyou';
import { FIELD_NAME } from './constants/form-constants';
import { AppProvider, useApp } from './contexts/app-context';
import { OneFormV2Context, useFeatureServices } from './contexts/oneformv2-context';
import { formService } from './form-service';
import { FormController } from './forms/form-controller';
import { getBrand } from './helpers/carline-helpers';
import {
	getFormName,
	getPageCategory,
	hasOfertaFromExternalContent,
	isCallMeForm,
	isInvertedThemeForm,
} from './helpers/form-helpers';
import { getEmail, getSurname1, getUserName } from './helpers/name-helper';
import { configProviderManager } from './service/config-provider';
import { externalContentManager } from './service/external-content';
import { styles } from './styles';
import { useTrackingManager } from './tracking/use-tracking-manager';

export function App(): JSX.Element {
	const {
		's2:history': historyService,
		's2:logger': logger,
		'service-config-provider': serviceConfigProvider,
		'fa-content-service': faContentService,
	} = useFeatureServices();

	useEffect(() => {
		console.log('🚀 ~ App ~ logger', logger);
		console.log('🚀 ~ App ~ historyService', historyService);
		console.log('🚀 ~ App ~ serviceConfigProvider', serviceConfigProvider);
		console.log('🚀 ~ App ~ faContentService', faContentService?.content);
	}, [logger, historyService, serviceConfigProvider, faContentService]);

	const { portalService } = useContext(OneFormV2Context);
	const [userData, setUserData] = React.useState<GetPersonUser | null>(null);

	React.useLayoutEffect(() => {
		const subscription = portalService.getPerson().subscribe({
			next: (data: GetPersonUser | null) => {
				setUserData(data);
			},
		});
		return () => subscription.unsubscribe();
	}, [portalService]);

	React.useLayoutEffect(() => {
		const userName = getUserName(userData);
		formService.setFieldValue(FIELD_NAME.NAME, userName);
		const userSurname1 = getSurname1(userData);
		formService.setFieldValue(FIELD_NAME.SURNAME_1, userSurname1);
		const userEmail = getEmail(userData);
		formService.setFieldValue(FIELD_NAME.EMAIL, userEmail);
	}, [userData]);

	const trackingManager = useTrackingManager();

	useEffect(() => {
		if (faContentService?.content) {
			if (!(faContentService.content as any).params) {
				(faContentService.content as any).params = {};
			}

			(faContentService.content as any).params.onCloseCallback = () => {
				trackingManager.trackFormCloseClick({ contentId: 'Cerrar' });
			};

			if (isCallMeForm()) {
				// El tracking de carga de call me lo lanza CallMeForm solo si está abierto
			} else if (hasOfertaFromExternalContent()) {
				// Tracking específico para lead venta oferta que espera VEHICLE_PRECONFIGURATION
				let tracked = false;
				const tracking = () => {
					if (tracked) return;
					const vehiclePreconfiguration = formService.getFieldValue(FIELD_NAME.VEHICLE_PRECONFIGURATION);
					if (vehiclePreconfiguration !== undefined) {
						trackingManager.trackFormLayerLoad(getFormName(), getBrand(), getPageCategory(), vehiclePreconfiguration);
						tracked = true;
					}
				};
				tracking();

				const unsubscribe = formService.subscribeToField(FIELD_NAME.VEHICLE_PRECONFIGURATION, { onChange: tracking });
				return () => unsubscribe();
			} else {
				// Tracking normal para otros formularios
				trackingManager.trackFormLayerLoad(getFormName(), getBrand(), getPageCategory());
			}
		}
		return () => {};
	}, [faContentService]);

	return (
		<AppProvider>
			<AppContent serviceConfigProvider={serviceConfigProvider} faContentService={faContentService} />
		</AppProvider>
	);
}

interface AppContentProps {
	serviceConfigProvider: any;
	faContentService: any;
}

const AppContent = ({ serviceConfigProvider, faContentService }: AppContentProps) => {
	const { loading, error } = useApp();
	const [isAppReady, setIsAppReady] = useState(false);

	useLayoutEffect(() => {
		configProviderManager.setConfigs(serviceConfigProvider?.configs as any);
		externalContentManager.setFaContent(faContentService?.content as any);
		formService.reset();
		setIsAppReady(true);
	}, [serviceConfigProvider, faContentService]);

	return (
		<div className="oneform">
			<ThemeProvider theme={isInvertedThemeForm() ? 'inverted' : 'main'}>
				<style>{styles}</style>
				{isAppReady ? (
					<>
						<Form />
						{/* Los errores los muestra Form */}
						{loading && !error && <LoadingScreen />}
					</>
				) : (
					<LoadingScreen />
				)}
			</ThemeProvider>
		</div>
	);
};

const Form = () => {
	const { error, success, thankyouScreen } = useApp();

	if (error) return <ErrorScreen />;
	if (success) return thankyouScreen || <ThankyouScreen />;

	return <FormController />;
};
